import { useState } from "react";
import { Box, Palette, Truck, Sparkles, Shield, Clock } from "lucide-react";
import InteriorsQuoteModal from "@/pages/interiors/InteriorsQuoteModal";

const benefits = [
  {
    icon: Box,
    title: "Extensive SA Range",
    description: "One of the largest serviced accommodation furniture selections in the UK, supported by a reliable UK-based supply chain. Every piece chosen for durability and guest appeal.",
  },
  {
    icon: Clock,
    title: "Furnished in 7 Days",
    description: "Your property fully furnished and styled within 7 working days anywhere on the UK mainland - making this the fastest route from empty shell to live Airbnb listing.",
  },
  {
    icon: Palette,
    title: "Expert-Led Design",
    description: "Led by Lisa Fox, with 30+ years of interior design experience and multiple Channel 4 features. From bold statement looks to calm neutral retreats - always guest-optimised.",
  },
  {
    icon: Sparkles,
    title: "STR-Optimised Styling",
    description: "Every collection is designed specifically for serviced accommodation: durable, easy-to-clean furniture, hotel-quality linen, and accessories that photograph beautifully.",
  },
  {
    icon: Shield,
    title: "3-Year Warranty",
    description: "All hard furniture is backed by a no-quibble 3-year warranty against manufacturing defects. Built to last in a high-turnover rental environment.",
  },
  {
    icon: Truck,
    title: "Flexible Payment Options",
    description: "Choose purchase, lease finance over 3–5 years, or a 60-day payment plan to protect your cash flow. Options offered subject to status.",
  },
];

const DEFAULT_ACTIVE = 1;

export default function WhyInteriors() {
  const [activeIndex, setActiveIndex] = useState(DEFAULT_ACTIVE);
  const [showModal, setShowModal] = useState(false);

  return (
    <section style={{ backgroundColor: "#fff", padding: "clamp(60px, 7vw, 96px) clamp(20px, 5vw, 80px)" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 11, fontWeight: 600, letterSpacing: "0.1em", textTransform: "uppercase", color: "#888", marginBottom: 12, marginTop: 0 }}>
            Why Truestays Interiors
          </p>
          <h2 style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "clamp(28px, 4vw, 44px)", letterSpacing: "-0.02em", lineHeight: 1.15, color: "#1A1A1A", margin: "0 auto 14px", maxWidth: 640 }}>
            A guest-ready property,<br />without lifting a finger.
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 16, color: "#666", lineHeight: 1.6, maxWidth: 520, margin: "0 auto" }}>
            We handle everything from design and sourcing to delivery and styling. The fastest way from empty shell to live on Airbnb - in as little as 7 days.
          </p>
        </div>

        <div className="why-interiors-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14 }}>
          {benefits.map(({ icon: Icon, title, description }, i) => {
            const isActive = activeIndex === i;
            return (
              <div
                key={title}
                onClick={() => setShowModal(true)}
                onMouseEnter={() => setActiveIndex(i)}
                style={{
                  background: isActive ? "#112215" : "#F7F7F5",
                  borderRadius: 14,
                  padding: "26px 26px 30px",
                  display: "flex",
                  flexDirection: "column",
                  cursor: "pointer",
                  transform: isActive ? "rotate(-1.5deg) scale(1.03)" : "rotate(0deg) scale(1)",
                  boxShadow: isActive ? "0 20px 50px rgba(0,0,0,0.22)" : "0 0 0 rgba(0,0,0,0)",
                  transition: "all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  zIndex: isActive ? 2 : 1,
                  position: "relative",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 44 }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: 16, color: isActive ? "#fff" : "#1A1A1A", letterSpacing: "-0.01em", transition: "color 0.25s" }}>
                    {title}
                  </span>
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: isActive ? "rgba(255,255,255,0.15)" : "#ECEAE6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "background 0.25s" }}>
                    <Icon size={16} color={isActive ? "#fff" : "#555"} strokeWidth={1.8} />
                  </div>
                </div>
                <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13.5, color: isActive ? "rgba(255,255,255,0.75)" : "#666", lineHeight: 1.65, margin: 0, transition: "color 0.25s" }}>
                  {description}
                </p>
              </div>
            );
          })}
        </div>

        <div style={{ textAlign: "center", marginTop: 40 }}>
          <button
            onClick={() => setShowModal(true)}
            style={{ background: "hsl(var(--primary))", color: "#fff", border: "none", borderRadius: 8, padding: "14px 28px", fontFamily: "'DM Sans', sans-serif", fontSize: 14, fontWeight: 600, cursor: "pointer", transition: "background 0.2s" }}
            onMouseEnter={e => e.currentTarget.style.background = "#C4C024"}
            onMouseLeave={e => e.currentTarget.style.background = "hsl(var(--primary))"}
          >
            Request a quote
          </button>
        </div>
      </div>

      {showModal && <InteriorsQuoteModal onClose={() => setShowModal(false)} onSubmitted={() => setShowModal(false)} />}

      <style>{`
        @media (max-width: 900px) { .why-interiors-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px) { .why-interiors-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}